import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { AdminActions } from "src/redux/Admin/actions";
import { AdminSelectors } from "src/redux/Admin/selectors";
import Button from "src/components/common/Button";
import CheckBox from "src/components/common/CheckBox";
import UserRow, { isOperator } from "./UserRow";
import useStagedUserChanges from "./useStagedUserChanges";
import styles from "./styles.module.scss";

const UserManagement = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    // /admin/users returns platform users only (ksl-admin realm). Cpty users
    // load per counterparty on the dedicated /users/:cptyCode page - never all
    // at once: the all-users sweep cost one Keycloak round-trip per user and
    // took tens of seconds against a large user population.
    const users = useSelector(AdminSelectors.Users);
    const loading = useSelector(AdminSelectors.UsersLoading);
    const error = useSelector(AdminSelectors.UsersError);
    const cpties = useSelector(AdminSelectors.Cpties);

    useEffect(() => {
        dispatch(AdminActions.FetchUsers());
        dispatch(AdminActions.FetchCpties());
    }, [dispatch]);

    // Platform accounts only (no cpty), operators listed before supervisors.
    const platform = useMemo(
        () =>
            (users || [])
                .filter((u) => !u.cpty)
                .sort((a, b) => (isOperator(b) ? 1 : 0) - (isOperator(a) ? 1 : 0)),
        [users],
    );

    // Kept across visits, and separate from the Counterparty Management page's
    // own setting: the two lists are read for different reasons.
    const [hideTest, setHideTest] = useState(
        () => localStorage.getItem("hideTestCptyUsers") === "true"
    );

    useEffect(() => {
        localStorage.setItem("hideTestCptyUsers", String(hideTest));
    }, [hideTest]);

    const liveCpties = useMemo(
        () => (cpties || []).filter((c) => c.lifecycle !== "OFF_BOARDING" && c.lifecycle !== "PURGING"),
        [cpties],
    );

    const activeCpties = useMemo(
        () => liveCpties.filter((c) => !(hideTest && c.testCpty)),
        [liveCpties, hideTest],
    );

    const hiddenTestCount = hideTest
        ? liveCpties.filter((c) => c.testCpty).length
        : 0;

    // Platform users support enable/disable/delete (no role switch); staged and
    // applied through the shared confirm/discard bar. Reload after apply.
    //
    // Platform accounts are not created here. An operator or supervisor is
    // seeded by ksl-deploy, which enrols the credential it needs at the same
    // time; a half-made account from this page could not sign in.
    const {
        pendingActions,
        onToggleEnabled,
        onDelete,
        applyChanges,
        clearStaged,
        pendingCount,
    } = useStagedUserChanges(() => dispatch(AdminActions.FetchUsers()));

    return (
        <div className={`${styles.userManagement} ${pendingCount > 0 ? styles.hasStatusBar : ""}`}>
            <div className={styles.header}>
                <div className={styles.title}>User Management</div>
            </div>

            {error && <div className={styles.error}>{error}</div>}

            {loading && (!users || users.length === 0) ? (
                <div className={styles.loading}>Loading users…</div>
            ) : (
                <>
                    <div className={styles.section}>
                        <div className={styles.sectionTitle}>Platform users</div>
                        <table className={styles.table} data-testid="platform-users-table">
                            <thead>
                                <tr>
                                    <th>Email</th><th>Name</th><th>Role</th><th>Status</th><th>2FA</th><th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {platform.length === 0 ? (
                                    <tr><td colSpan={6} className={styles.empty}>No platform users</td></tr>
                                ) : platform.map((u) => (
                                    <UserRow
                                        key={u.realm + u.id}
                                        user={u}
                                        onToggleEnabled={onToggleEnabled}
                                        onDelete={onDelete}
                                        onShowHistory={(usr) =>
                                            history.push({
                                                pathname: `/users/${usr.cpty || "platform"}/${usr.id}/history`,
                                                state: { user: usr },
                                            })
                                        }
                                        pendingAction={pendingActions.get(u.id)?.kind}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className={styles.section}>
                        <div className={styles.sectionHead}>
                            <div className={styles.sectionTitle}>Counterparty users</div>
                            <CheckBox
                                name="hideTestCptyUsers"
                                selected={hideTest}
                                onChange={(e) => setHideTest(e.target.checked)}
                                small
                                styleName="black"
                                dataTestId="hide-test-cpty-users-toggle"
                            >
                                Hide test cpties
                                {hiddenTestCount > 0 && (
                                    <span className={styles.hiddenCount}>
                                        ({hiddenTestCount} hidden)
                                    </span>
                                )}
                            </CheckBox>
                        </div>
                        <table className={styles.table} data-testid="cpty-user-list">
                            <thead>
                                <tr><th>Code</th><th>Name</th><th>Actions</th></tr>
                            </thead>
                            <tbody>
                                {activeCpties.length === 0 ? (
                                    <tr><td colSpan={3} className={styles.empty}>No counterparties</td></tr>
                                ) : activeCpties.map((c) => (
                                    <tr key={c.cptyCode} data-testid={`cpty-users-row-${c.cptyCode}`}>
                                        <td>{c.cptyCode}</td>
                                        <td>{c.cptyName}</td>
                                        <td>
                                            <Button
                                                value="Manage Users"
                                                dataTestId={`manage-users-${c.cptyCode}`}
                                                onClick={() => history.push(`/users/${c.cptyCode}`)}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {pendingCount > 0 && (
                <div className={styles.statusBar}>
                    <span>{pendingCount} change{pendingCount > 1 ? "s" : ""} pending</span>
                    <Button
                        value="Discard Changes"
                        dataTestId="discard-user-changes"
                        classes={[styles.detailBtn]}
                        onClick={clearStaged}
                    />
                    <Button
                        value="Confirm Changes"
                        dataTestId="apply-user-changes"
                        classes={[styles.removeBtn]}
                        onClick={applyChanges}
                    />
                </div>
            )}
        </div>
    );
};

export default UserManagement;
